import { inject, Inject, Injectable, Injector } from '@angular/core';
import EstensaModel, { AuthenticationData, EWebViewerType, ImagingOpenParams } from 'estensa-module';
import { catchError, filter, firstValueFrom, from, map, of, switchMap, tap } from 'rxjs';

import { RadioSettingsService } from '../settings/radio-settings.service';
import { fromNg1OpenParams, KeyImage, PacsStudyDataVnext, toNg1PacsStudyData } from './common';
import { EstensaReviewViewerHelper } from './estensa-review/estensa-review';
import { ZefiroViewerHelper } from './zefiro/zefiro.helper';
import {
    IImagingServiceNg1,
    IViewerHelper,
    MammographySettings,
    RADIOLOGY_API_CLIENT_IMAGING_SERVICE,
} from './angularjs';
import {
    ExaminationStatusForImaging,
    ModelShowImage,
    ModelUSMeasureShow,
    ModelUSStressEchoAssociateImages,
} from './ebit-viewer-api';
import { CustomPageType } from '../../custom-pages/cp-enum';
import { NavigationEnd, Router } from '@angular/router';
import { BrokerCheckService } from '../angularjs/broker-check.service';
import { VueClientPhilipsViewerHelper } from './vueclient-philips/vueclient-philips-viewer-helper.service';
import { Logger } from '@se/se.platform.base';

@Injectable({
    providedIn: 'root',
})
export class ImagingService {
    private _viewerHelper: IViewerHelper | undefined = undefined; //referenza al viewer attualmente aperto
    private _genericViewerHelper: IViewerHelper | undefined = undefined;
    private _viewerHelperCardio: IViewerHelper | undefined = undefined;
    private _viewerHelperRadio: IViewerHelper | undefined = undefined;

    private sourceViewerType: 'generic' | 'cardio' | 'radio' = 'generic';

    private viewerType: EWebViewerType | undefined = undefined; //tipo del viewer attualmente aperto
    private genericViewerType: EWebViewerType | undefined = undefined;
    private cardioViewerType: EWebViewerType | undefined = undefined;
    private radioViewerType: EWebViewerType | undefined = undefined;

    private initCompleted = false;

    private readonly vueClientPhilipsService = inject(VueClientPhilipsViewerHelper);

    static readonly VIEWERS_WITHOUT_APPEND_AND_DISCARD_SUPPORT: ReadonlySet<EWebViewerType> = new Set([
        EWebViewerType.Calgary,
        EWebViewerType.SecureCalgary,
        EWebViewerType.Sectra,
        EWebViewerType.Fuji,
        EWebViewerType.OsiriX,
        EWebViewerType.Vna,
        EWebViewerType.Synapse7,
        EWebViewerType.VueMotion,
        EWebViewerType.VueClient,
        EWebViewerType.GenericViewer,
    ]);

    static readonly VIEWERS_WITH_APPEND_AND_DISCARD_SUPPORT: ReadonlySet<EWebViewerType> = new Set([
        EWebViewerType.Neologica,
        EWebViewerType.eUnity,
        EWebViewerType.EstensaReviewVNext,
        EWebViewerType.EstensaReview,
        EWebViewerType.SeWebViewer,
        EWebViewerType.ZEfiRO,
    ]);

    private viewerHelpersMap: Record<EWebViewerType, IViewerHelper | undefined> = {
        [EWebViewerType.Neologica]: undefined,
        [EWebViewerType.Calgary]: undefined,
        [EWebViewerType.SecureCalgary]: undefined,
        [EWebViewerType.Fuji]: undefined,
        [EWebViewerType.Sectra]: undefined,
        [EWebViewerType.GenericViewer]: undefined,
        [EWebViewerType.EstensaReview]: undefined,
        [EWebViewerType.EstensaReviewVNext]: undefined,
        [EWebViewerType.OsiriX]: undefined,
        [EWebViewerType.eUnity]: undefined,
        [EWebViewerType.VueMotion]: undefined,
        [EWebViewerType.Synapse7]: undefined,
        [EWebViewerType.Vna]: undefined,
        [EWebViewerType.SeWebViewer]: undefined,
        [EWebViewerType.ZEfiRO]: undefined,
        [EWebViewerType.VueClient]: undefined,
        [EWebViewerType.Hologic]: undefined,
    };

    private initializedViewerHelpers: Record<EWebViewerType, boolean> = {
        [EWebViewerType.Neologica]: false,
        [EWebViewerType.Calgary]: false,
        [EWebViewerType.SecureCalgary]: false,
        [EWebViewerType.Fuji]: false,
        [EWebViewerType.Sectra]: false,
        [EWebViewerType.GenericViewer]: false,
        [EWebViewerType.EstensaReview]: false,
        [EWebViewerType.EstensaReviewVNext]: false,
        [EWebViewerType.OsiriX]: false,
        [EWebViewerType.eUnity]: false,
        [EWebViewerType.VueMotion]: false,
        [EWebViewerType.Synapse7]: false,
        [EWebViewerType.Vna]: false,
        [EWebViewerType.SeWebViewer]: false,
        [EWebViewerType.ZEfiRO]: false,
        [EWebViewerType.VueClient]: false,
        [EWebViewerType.Hologic]: undefined,
    };

    //aggiungere condizione su dedicated for mammo che ritorni estensaReview versione vecchia a prescindere da isWebApiEnabled
    static getViewerTypeBySettings(
        viewer: EWebViewerType,
        settings: EstensaModel.RadioSettings,
        mammographySettings: MammographySettings,
    ): EWebViewerType {
        if (
            mammographySettings.enableDedicatedWebViewerForMammography &&
            mammographySettings.mammographicViewer === EstensaModel.MammographicViewerType.EstensaReview
        ) {
            return EWebViewerType.EstensaReview;
        }
        if (viewer === EWebViewerType.EstensaReview && settings.extensaSettings.isWebApiEnabled) {
            return EWebViewerType.EstensaReviewVNext;
        }
        return viewer;
    }

    static readonly DISCARD_IMAGES_VIEWERS: EWebViewerType[] = [
        EWebViewerType.eUnity,
        EWebViewerType.ZEfiRO,
        EWebViewerType.EstensaReviewVNext,
        EWebViewerType.Hologic,
    ];

    constructor(
        @Inject(RADIOLOGY_API_CLIENT_IMAGING_SERVICE) private imagingServiceNg1: IImagingServiceNg1,
        private injector: Injector,
        private radioSettingsService: RadioSettingsService,
        private router: Router,
        private brokerCheckService: BrokerCheckService,
        private logger: Logger,
    ) {}

    get viewerHelper() {
        return this._viewerHelper;
    }

    get genericViewerHelper() {
        return this._genericViewerHelper;
    }

    get cardioViewerHelper() {
        return this._viewerHelperCardio;
    }

    get radioViewerHelper() {
        return this._viewerHelperRadio;
    }

    async setAndInitViewerHelper(viewerType: EWebViewerType): Promise<IViewerHelper | undefined> {
        if (this.viewerHelpersMap[viewerType] && !this.initializedViewerHelpers[viewerType]) {
            await this.viewerHelpersMap[viewerType]?.init();
            this.initializedViewerHelpers[viewerType] = true;
        }

        return this.viewerHelpersMap[viewerType];
    }

    async initService() {
        if (this.initCompleted) {
            return;
        }

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                switchMap((routerEvent: NavigationEnd) =>
                    from(this.getCurrentViewerType()).pipe(
                        map((currentViewerType) => ({ routerEvent, currentViewerType })),
                    ),
                ),
                switchMap(({ routerEvent, currentViewerType }) => {
                    console.log({ url: routerEvent.url, currentViewerType });
                    if (routerEvent.url === '/menu') {
                        //quando navigo nel menu principale scarico le immagini se possibile altrimenti chiudo il viewer
                        return from(this.discardOrCloseViewer()).pipe(
                            tap((viewerClosed) => (viewerClosed ? this.activateGenericViewer() : false)),
                            map((viewerClosed) => ({
                                action: viewerClosed ? 'close' : 'discard',
                                routerEvent,
                                currentViewerType,
                            })),
                        );
                    } else if (routerEvent.url.includes('/cardio-visit/worklist')) {
                        return from(this.handleViewerActivation('cardio')).pipe(
                            map(() => ({ action: 'activate-cardio', routerEvent, currentViewerType })),
                        );
                    } else if (routerEvent.url.includes('/specialty-reporting/worklist')) {
                        return from(this.handleViewerActivation('radio')).pipe(
                            map(() => ({ action: 'activate-radio', routerEvent, currentViewerType })),
                        );
                    } else {
                        return of({ action: 'none', routerEvent, currentViewerType });
                    }
                }),
                catchError((error) => {
                    console.error('Error handling viewer navigation:', error);
                    return of(null);
                }),
            )
            .subscribe((result) => {
                if (result) {
                    this.logger.debug('Viewer navigation handled:', JSON.stringify(result));
                }
            });

        /*questi sono i viewer helpers disponibili in vnext, quindi se una entry nella mappa this.viewerHelpersMap
         * fosse undefined il corrispondente helper potrebbe essere gestito (se presente) nel servizio this.imagingServiceNg1*/
        this.viewerHelpersMap[EWebViewerType.EstensaReviewVNext] = this.injector.get(EstensaReviewViewerHelper);
        this.viewerHelpersMap[EWebViewerType.EstensaReview] = this.viewerHelpersMap[EWebViewerType.EstensaReview];
        this.viewerHelpersMap[EWebViewerType.SeWebViewer] = this.injector.get(ZefiroViewerHelper);
        this.viewerHelpersMap[EWebViewerType.ZEfiRO] = this.viewerHelpersMap[EWebViewerType.SeWebViewer];
        this.viewerHelpersMap[EWebViewerType.VueClient] = this.injector.get(VueClientPhilipsViewerHelper);
        //nella vecchia refertazione o nella cartella paziente -- servizio angular js per viewer
        const genericViewerType = await this.getGenericViewerType();

        this._genericViewerHelper = await this.setAndInitViewerHelper(genericViewerType);

        //da aprire in automatico solo quando si entra nella worklist non al login
        const cardioViewerType = await this.getCardioViewerType();
        this._viewerHelperCardio = await this.setAndInitViewerHelper(cardioViewerType);
        const radioViewerType = await this.getRadioViewerType();
        this._viewerHelperRadio = await this.setAndInitViewerHelper(radioViewerType);

        if (this.genericViewerHelper) {
            this.imagingServiceNg1.setViewerHelper(this.genericViewerHelper);
            this.imagingServiceNg1.setGenericViewerHelper(this.genericViewerHelper);
        }
        if (this.cardioViewerHelper) {
            this.imagingServiceNg1.setCardioViewerHelper(this.cardioViewerHelper);
        }
        if (this.radioViewerHelper) {
            this.imagingServiceNg1.setRadioViewerHelper(this.radioViewerHelper);
        }

        this.initCompleted = true;
    }

    async isViewerOpen() {
        return this._viewerHelper ? await this._viewerHelper?.isViewerOpen() : this.imagingServiceNg1.isViewerOpen();
    }

    async getViewerTypeByCp(cp: CustomPageType | undefined): Promise<EWebViewerType> {
        switch (cp) {
            case CustomPageType.CARDIOLOGY_VISIT:
            case CustomPageType.ULTRASOUND:
                return this.getCardioViewerType();
            case CustomPageType.SPECIALTY:
                return this.getRadioViewerType();
            default:
                return this.getGenericViewerType();
        }
    }

    async getCurrentViewerType(): Promise<EWebViewerType> {
        switch (this.sourceViewerType) {
            case 'cardio':
                return this.getCardioViewerType();
            case 'radio':
                return this.getRadioViewerType();
            default:
                return this.getGenericViewerType();
        }
    }
    isEstensaReviewForMammoActive = () => {
        const mammographySettings = this.imagingServiceNg1.getMammographySettings();
        return (
            mammographySettings.enableDedicatedWebViewerForMammography &&
            mammographySettings.mammographicViewer === EstensaModel.MammographicViewerType.EstensaReview
        );
    };

    async getGenericViewerType(): Promise<EWebViewerType> {
        if (this.genericViewerType) {
            return this.genericViewerType;
        }
        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);
        const viewer = settings.viewerSettings?.webViewerType;
        if (!viewer) {
            console.error('ViewerSettings not found for generic viewer in viewerSettings', settings.viewerSettings);
            this.logger.error('ViewerSettings not found for generic viewer in viewerSettings');
            throw new Error('ViewerSettings not found for generic viewer in viewerSettings');
        }
        const mammographySettings = this.imagingServiceNg1.getMammographySettings();
        this.genericViewerType = ImagingService.getViewerTypeBySettings(viewer, settings, mammographySettings);
        return this.genericViewerType;
    }

    async getCardioViewerType(): Promise<EWebViewerType> {
        if (this.cardioViewerType) {
            return this.cardioViewerType;
        }
        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);
        const viewer = settings.reportSettings.dedicatedWebViewerForVNextCardiology;
        const mammographySettings = this.imagingServiceNg1.getMammographySettings();
        this.cardioViewerType =
            ImagingService.getViewerTypeBySettings(viewer, settings, mammographySettings) ?? this.genericViewerType;
        return this.cardioViewerType;
    }

    async getRadioViewerType(): Promise<EWebViewerType> {
        if (this.radioViewerType) {
            return this.radioViewerType;
        }
        const settings = await firstValueFrom(this.radioSettingsService.radioSettingsAsync);
        const viewer = settings.reportSettings.dedicatedWebViewerForVNextRadiology;
        const mammographySettings = this.imagingServiceNg1.getMammographySettings();
        this.radioViewerType =
            ImagingService.getViewerTypeBySettings(viewer, settings, mammographySettings) ?? this.genericViewerType;
        return this.radioViewerType;
    }

    async areImagesOpen() {
        // Se è un viewer VNEXT, aperto e con immagini caricate, oppure se è un viewer AngularJS con flag a true
        if (
            (this.viewerHelper &&
                (await this.viewerHelper.isViewerOpen()) &&
                (await this.viewerHelper.areImagesOpen())) ||
            this.imagingServiceNg1.areImagesOpen
        ) {
            return true;
        } else {
            return false;
        }
    }

    public checkViewerForAppendSupport(action: EstensaModel.ImageActionType): EstensaModel.ImageActionType {
        let viewerType: EWebViewerType | undefined;
        if (this.viewerHelper) {
            viewerType = this.viewerHelper.viewerType as EWebViewerType;
        } else {
            // Ng1
            viewerType = this.imagingServiceNg1.getViewerType() as EWebViewerType;
        }

        if (ImagingService.VIEWERS_WITHOUT_APPEND_AND_DISCARD_SUPPORT.has(viewerType)) {
            return EstensaModel.ImageActionType.Discard;
        }

        if (ImagingService.VIEWERS_WITH_APPEND_AND_DISCARD_SUPPORT.has(viewerType)) {
            return action;
        }

        // Default behavior for unknown viewers
        return action;
    }

    /**
     * metodo chiamato solo in root.effects da action seOpenViewer senza parametri
     * scatenata solo da se-header-connectivity.component.ts. pulsante cliccabile solo se viewer vnext e` attivo
     * quindi non fa fallback su angularjs
     * @param openerSource
     */
    async openViewer(openerSource: 'generic' | 'cardio' | 'radio'): Promise<void> {
        /*verificare quale tipo va aperto
         * verificare se c'e gia un viewer aperto e se diverso chiuderlo*/
        let viewerTypeToOpen: EWebViewerType | null = null;
        if (openerSource === 'cardio') {
            viewerTypeToOpen = await this.getCardioViewerType();
        } else if (openerSource === 'radio') {
            viewerTypeToOpen = await this.getRadioViewerType();
        } else {
            //generic
            viewerTypeToOpen = await this.getGenericViewerType();
        }

        if (!this.viewerType || this.viewerType === viewerTypeToOpen) {
            //se e` gia aperto la chiamata ad openViewer non fa nulla.
            this.viewerType = viewerTypeToOpen;
            this._viewerHelper = this.viewerHelpersMap[viewerTypeToOpen];
        }

        if (this.viewerType !== viewerTypeToOpen) {
            //chiudere il viewer corrente ed aprire quello nuovo
            await this._viewerHelper?.closeViewer();
            this.viewerType = viewerTypeToOpen;
            this._viewerHelper = this.viewerHelpersMap[viewerTypeToOpen];
        }

        /*this._viewerHelper viene settato solo se aperto da qua!!!
         * altrimenti tutti i riferimenti al viewer attualmente attivo passano da this.imagingServiceNg1*/
        return this.viewerHelper?.openViewer();
    }

    /**
     * ritorna true se il viewer e` stato chiuso
     */
    async discardOrCloseViewer(): Promise<boolean> {
        let viewerType: EWebViewerType | undefined;
        if (this.viewerHelper) {
            viewerType = this.viewerHelper.viewerType as EWebViewerType;
        } else {
            // Ng1
            viewerType = this.imagingServiceNg1.getViewerType() as EWebViewerType;
        }

        if (ImagingService.VIEWERS_WITH_APPEND_AND_DISCARD_SUPPORT.has(viewerType)) {
            console.log({ viewerType }, 'request discard images');
            await this.discardImages(false);
            return false;
        }

        if (ImagingService.VIEWERS_WITHOUT_APPEND_AND_DISCARD_SUPPORT.has(viewerType)) {
            console.log({ viewerType }, 'request close viewer');
            await this.closeViewer();
            return true;
        }

        return false;
    }

    async closeViewer() {
        if (this.viewerHelper && this.viewerType) {
            await this.viewerHelper?.closeViewer();
            this.viewerType = undefined;
            this._viewerHelper = undefined;
        } else {
            this.imagingServiceNg1.closeViewer();
        }
        return;
    }

    async discardImages(askConfirmation?: boolean) {
        try {
            if (this.viewerHelper) {
                await this.viewerHelper.discardImages(askConfirmation);
            } else {
                await this.imagingServiceNg1.discardImages(askConfirmation);
            }
        } catch {
            /* Ignore these errors */
        }
    }

    setExaminationStatus(idExaminations: string[], status: ExaminationStatusForImaging, studyInstanceUIDs: string[]) {
        return this.viewerHelper?.setExaminationStatus?.(idExaminations, status, studyInstanceUIDs);
    }

    deleteKeyImages(images: KeyImage[]): Promise<unknown> {
        const promises = images.map((img) =>
            this.viewerHelper?.deleteKeyImage?.({
                instanceUID: img.SOPInstanceUID,
                isDeletable: true,
                nPla: null,
                repositoryKey: img.RepositoryKey,
                requestDeleteAll: false,
                requestRefreshIcon: true,
            }),
        );
        return Promise.all(promises);
    }

    usStressEchoSelectStage(stage: string) {
        this.viewerHelper?.usStressEchoSelectStage?.(stage);
    }

    usStressEchoSelectView(view: string) {
        this.viewerHelper?.usStressEchoSelectView?.(view);
    }

    uSStressEchoAssociateImages(model: ModelUSStressEchoAssociateImages) {
        this.viewerHelper?.uSStressEchoAssociateImages?.(model);
    }

    showImage(model: ModelShowImage) {
        this.viewerHelper?.showImage?.(model);
    }

    usMeasureShow(model: ModelUSMeasureShow) {
        this.viewerHelper?.usMeasureShow?.(model);
    }

    async viewImages(
        data: PacsStudyDataVnext[],
        credentials: AuthenticationData | null,
        openParam: EstensaModel.ImagingOpenParams,
    ): Promise<void> {
        if (this.viewerHelper) {
            return this.viewerHelper.openImages(data, fromNg1OpenParams(openParam ?? {}));
        }

        // Ho dati vNext da dare a servizio ng1.
        // await necessaria per passare da void[] a void.
        //await Promise.all(data.map((d) => this.imagingServiceNg1.openImages(toNg1PacsStudyData(d), credentials, openParam)));
        const _toNg1PacsStudyData = data.map((d) => {
            return toNg1PacsStudyData(d);
        });

        await this.imagingServiceNg1.openImages(_toNg1PacsStudyData, credentials, { ...openParam });
    }

    activateViewerByExaminations(/*examinationIds: string[], */ customPageType: CustomPageType | undefined | null) {
        /* attivo il viewer corretto su angularjs (potrebbe anche essere stato configurato da questo servizio
         * comunque bisogna passare dal broker quindi il tutto va attivato da angularjs*/
        switch (customPageType) {
            case CustomPageType.CARDIOLOGY_VISIT:
            case CustomPageType.ULTRASOUND:
                this.activateCardioViewer();
                break;
            case CustomPageType.SPECIALTY:
                this.activateRadioViewer();
                break;
            default:
                this.activateGenericViewer();
                break;
        }
    }

    /* questa funzione non viene usata in tutto il progetto !!!!!!! */
    async openImagesByByEprEvent(
        customPageType: CustomPageType | null,
        eprEvents: EstensaModel.RadioEprEvent[],
        credentials: AuthenticationData,
        openParams: ImagingOpenParams,
    ) {
        if (!(await this.isViewerOpen())) {
            switch (customPageType) {
                case CustomPageType.CARDIOLOGY_VISIT:
                case CustomPageType.ULTRASOUND:
                    this.activateCardioViewer();
                    break;
                case CustomPageType.SPECIALTY:
                    this.activateRadioViewer();
                    break;
                default:
                    this.activateGenericViewer();
                    break;
            }
        }

        /* questo chiama il vecchio broker Ng1...chiamare action seOpenImagesByEprEvent */
        this.brokerCheckService.openImagesByEprEvent(eprEvents, credentials, openParams);
    }

    activateCardioViewer() {
        this.sourceViewerType = 'cardio';
        this._viewerHelper = this.cardioViewerHelper;
        this.imagingServiceNg1.activateCardioViewer();
    }

    activateRadioViewer() {
        this.sourceViewerType = 'radio';
        this._viewerHelper = this.radioViewerHelper;
        this.imagingServiceNg1.activateRadioViewer();
    }

    activateGenericViewer() {
        this.sourceViewerType = 'generic';
        this._viewerHelper = this.genericViewerHelper;
        this.imagingServiceNg1.activateGenericViewer();
    }

    /**
     * apertura viewer senza immagini (solo viewers VNEXT)
     * @param viewerSource tipo di viewer da attivare
     */
    private async handleViewerActivation(viewerSource: 'generic' | 'cardio' | 'radio'): Promise<void> {
        const targetViewerType = await this.getViewerTypeBySource(viewerSource);
        const currentViewerType = await this.getCurrentViewerType();
        // chiudo il viewer se non è lo stesso tipo di viewer da attivare
        if (currentViewerType !== targetViewerType) {
            this.logger.debug(`Switching viewer from ${currentViewerType} to ${targetViewerType}`);
            await this.closeViewer();
        }
        // attivo il viewer
        switch (viewerSource) {
            case 'cardio':
                this.activateCardioViewer();
                break;
            case 'radio':
                this.activateRadioViewer();
                break;
            default:
                this.activateGenericViewer();
                break;
        }

        //apro il viewer senza immagini
        return this.openViewer(viewerSource);
    }

    /**
     * Get viewer type based on source
     */
    private async getViewerTypeBySource(source: 'generic' | 'cardio' | 'radio'): Promise<EWebViewerType> {
        switch (source) {
            case 'cardio':
                return this.getCardioViewerType();
            case 'radio':
                return this.getRadioViewerType();
            default:
                return this.getGenericViewerType();
        }
    }
}
